import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { type AriStateDay, useApi } from '@/shared/api';
import { propertyKeys } from '../queries';

/**
 * Die Daten des Preise-Reiters: Apartment, Belegung der nächsten 90 Tage und die zuletzt
 * übertragenen Zeiträume. Steht getrennt von der Fläche, damit dort nur noch Darstellung liegt.
 * Die Kalenderabfrage geht mit hinaus: solange sie lädt oder scheitert, ist nichts „frei“.
 */
export function usePreiseTabData(id: string) {
  const api = useApi();

  const { data: property } = useQuery({
    queryKey: propertyKeys.detail(id),
    queryFn: () => api.properties.get(id),
  });

  const calendarRange = useMemo(
    () => rangeFromObjectToday(property?.timezone),
    [property?.timezone],
  );

  const calendarQuery = useQuery({
    queryKey: [
      'availability-distribution',
      property?.propertyGroupId,
      calendarRange.from,
      calendarRange.to,
    ],
    queryFn: () =>
      api.availabilityDistribution.calendar(
        property!.propertyGroupId!,
        calendarRange.from,
        calendarRange.to,
      ),
    staleTime: 60_000,
    enabled: Boolean(property?.propertyGroupId),
  });
  const calendar = calendarQuery.data;
  const bookingsByDate = useMemo(() => {
    const apartment = calendar?.apartments.find((item) => item.propertyId === id);
    const bookedDates = new Set<string>();
    apartment?.bookings.forEach((booking) => {
      for (let date = booking.start; date < booking.endExclusive; date = addDays(date, 1)) {
        bookedDates.add(date);
      }
    });
    return new Map(
      (apartment?.days ?? []).map((day) => [
        day.date,
        {
          date: day.date,
          status: bookedDates.has(day.date) ? 'booked' : 'free',
          occupiedByFamily: day.closedByFamily === true,
        },
      ]),
    );
  }, [calendar, id]);
  const ariDays: AriStateDay[] = useMemo(() => {
    const apartment = calendar?.apartments.find((item) => item.propertyId === id);
    return (apartment?.days ?? []).map((day) => ({
      date: day.date,
      rate: day.rate,
      minStay: day.minStay,
      maxStay: day.maxStay,
      stopSell: day.stopSell,
      closedToArrival: day.closedToArrival ?? null,
      closedToDeparture: day.closedToDeparture ?? null,
      availability: day.available ? 1 : 0,
    }));
  }, [calendar, id]);

  // Gleicher Schlüssel wie in refreshChannex: eine bestätigte Übertragung lädt den Verlauf neu.
  const history = useQuery({
    queryKey: ['channex', 'ariPushHistory', id],
    queryFn: () => api.channex.ariPushHistory(id, 20),
  });

  return { property, bookingsByDate, ariDays, history, calendarQuery };
}

function rangeFromObjectToday(timezone = 'Europe/Berlin') {
  const formatter = new Intl.DateTimeFormat('en', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = Object.fromEntries(
    formatter.formatToParts(new Date()).map((part) => [part.type, part.value]),
  );
  const from = `${parts.year}-${parts.month}-${parts.day}`;
  return { from, to: addDays(from, 89) };
}

function addDays(iso: string, days: number) {
  const date = new Date(`${iso}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}
